/**
 * CR314: MerchantMark Component
 * Renders merchant logo with category icon fallback
 * 
 * Priority:
 * 1. logo_svg (if present)
 * 2. logo_url (if present and loads successfully)
 * 3. Category icon (fallback when enabled)
 * 4. Store placeholder icon
 */

import { useState } from "react";
import { Store } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CategoryIconData } from "@/hooks/useCategoryIconMap";

interface MerchantMarkProps {
  /** Merchant display name for alt text */
  displayName: string;
  /** Inline SVG markup (highest priority) */
  logoSvg?: string | null;
  /** Logo URL (second priority) */
  logoUrl?: string | null;
  /** Category icon data for fallback */
  categoryIcon?: CategoryIconData | null;
  /** Whether to use category icon as fallback */
  fallbackEnabled: boolean;
  /** Container size class */
  size?: "sm" | "md";
}

export function MerchantMark({
  displayName,
  logoSvg,
  logoUrl,
  categoryIcon,
  fallbackEnabled,
  size = "md",
}: MerchantMarkProps) {
  const [imgFailed, setImgFailed] = useState(false);

  // Reset imgFailed when logoUrl changes
  const [prevLogoUrl, setPrevLogoUrl] = useState(logoUrl);
  if (logoUrl !== prevLogoUrl) {
    setPrevLogoUrl(logoUrl);
    setImgFailed(false);
  }

  const containerClass = cn(
    "rounded bg-muted flex items-center justify-center overflow-hidden",
    size === "sm" ? "w-6 h-6" : "w-8 h-8"
  );
  const iconClass = size === "sm" ? "w-4 h-4" : "w-6 h-6";
  const placeholderIconClass = size === "sm" ? "h-3 w-3" : "h-4 w-4";

  // Priority 1: Inline SVG
  if (logoSvg) {
    return (
      <div className={containerClass}>
        <div
          className={iconClass}
          dangerouslySetInnerHTML={{ __html: logoSvg }}
        />
      </div>
    );
  }

  // Priority 2: Logo URL (if not failed)
  if (logoUrl && !imgFailed) {
    return (
      <div className={containerClass}>
        <img
          src={logoUrl}
          alt={displayName}
          className={cn(iconClass, "object-contain")}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  // Priority 3: Category icon (if fallback enabled)
  if (fallbackEnabled && categoryIcon) {
    // Category icon priority: svg > url
    if (categoryIcon.icon_svg) {
      return (
        <div className={containerClass}>
          <div
            className={iconClass}
            dangerouslySetInnerHTML={{ __html: categoryIcon.icon_svg }}
          />
        </div>
      );
    }
    if (categoryIcon.icon_url) {
      return (
        <div className={containerClass}>
          <img
            src={categoryIcon.icon_url}
            alt={categoryIcon.name}
            className={cn(iconClass, "object-contain")}
          />
        </div>
      );
    }
  }

  // Priority 4: Placeholder (store icon or empty space)
  return (
    <div className={containerClass}>
      <Store className={cn(placeholderIconClass, "text-muted-foreground")} />
    </div>
  );
}
